<template>
  <div class="modal fade" id="serviceFormModal" tabindex="-1" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i :class="isEditMode ? 'bx bx-edit' : 'bx bx-plus-circle'" class="me-2"></i>
            {{ isEditMode ? 'Edit Service' : 'Add New Service' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" @click="resetForm"></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="saveService">
            <!-- Nav tabs -->
            <ul class="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
              <li class="nav-item">
                <a class="nav-link active" data-bs-toggle="tab" href="#basic-info" role="tab">
                  <span class="d-block d-sm-none"><i class="bx bx-info-circle"></i></span>
                  <span class="d-none d-sm-block">Basic Information</span>
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#medical-codes" role="tab">
                  <span class="d-block d-sm-none"><i class="bx bx-code"></i></span>
                  <span class="d-none d-sm-block">Medical Codes</span>
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#pricing-coverage" role="tab">
                  <span class="d-block d-sm-none"><i class="bx bx-money"></i></span>
                  <span class="d-none d-sm-block">Pricing & Coverage</span>
                </a>
              </li>
            </ul>

            <!-- Tab panes -->
            <div class="tab-content p-3 text-muted">
              <!-- Basic Information Tab -->
              <div class="tab-pane active" id="basic-info" role="tabpanel">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Service Code <span class="text-danger">*</span></label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.service_code"
                        placeholder="e.g., CONS-001"
                        required
                        :class="{ 'is-invalid': errors.service_code }">
                    <div class="invalid-feedback" v-if="errors.service_code">
                      {{ errors.service_code }}
                    </div>
                    <small class="form-text text-muted">Unique identifier for the service</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Category <span class="text-danger">*</span></label>
                    <select
                        class="form-select"
                        v-model="formData.service_category"
                        required
                        :class="{ 'is-invalid': errors.service_category }">
                      <option value="">Select Category</option>
                      <option value="Consultation">Consultation</option>
                      <option value="Laboratory">Laboratory</option>
                      <option value="Radiology">Radiology</option>
                      <option value="Procedure">Procedure</option>
                      <option value="Medication">Medication</option>
                      <option value="Room">Room</option>
                      <option value="Other">Other</option>
                    </select>
                    <div class="invalid-feedback" v-if="errors.service_category">
                      {{ errors.service_category }}
                    </div>
                  </div>

                  <div class="col-12 mb-3">
                    <label class="form-label">Service Name <span class="text-danger">*</span></label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.service_name"
                        placeholder="e.g., General Consultation"
                        required
                        :class="{ 'is-invalid': errors.service_name }">
                    <div class="invalid-feedback" v-if="errors.service_name">
                      {{ errors.service_name }}
                    </div>
                  </div>

                  <div class="col-12 mb-3">
                    <label class="form-label">Description</label>
                    <textarea
                        class="form-control"
                        v-model="formData.description"
                        rows="3"
                        placeholder="Detailed description of the service..."></textarea>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Status</label>
                    <div class="form-check form-switch">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          v-model="formData.is_active"
                          :true-value="1"
                          :false-value="0">
                      <label class="form-check-label">
                        {{ formData.is_active == 1 ? 'Active' : 'Inactive' }}
                      </label>
                    </div>
                    <small class="form-text text-muted">Active services can be used in claims</small>
                  </div>
                </div>
              </div>

              <!-- Medical Codes Tab -->
              <div class="tab-pane" id="medical-codes" role="tabpanel">
                <div class="alert alert-info mb-3">
                  <i class="bx bx-info-circle me-1"></i>
                  Medical codes help in standardizing services and determining coverage
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">ICD Code</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.icd_code"
                        placeholder="e.g., Z00.00">
                    <small class="form-text text-muted">International Classification of Diseases</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">CPT Code</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.cpt_code"
                        placeholder="e.g., 99213">
                    <small class="form-text text-muted">Current Procedural Terminology</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">SNOMED Code</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.snomed_code"
                        placeholder="e.g., 162673000">
                    <small class="form-text text-muted">SNOMED Clinical Terms</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Local Code</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.local_code"
                        placeholder="e.g., ETH-001">
                    <small class="form-text text-muted">Ethiopia-specific code</small>
                  </div>
                </div>

                <div class="card bg-light">
                  <div class="card-body">
                    <h6 class="card-title mb-3">Code Lookup Tools</h6>
                    <div class="d-flex gap-2">
                      <button type="button" class="btn btn-sm btn-outline-primary" @click="lookupICD">
                        <i class="bx bx-search me-1"></i>ICD Lookup
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-info" @click="lookupCPT">
                        <i class="bx bx-search me-1"></i>CPT Lookup
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-warning" @click="lookupSNOMED">
                        <i class="bx bx-search me-1"></i>SNOMED Lookup
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pricing & Coverage Tab -->
              <div class="tab-pane" id="pricing-coverage" role="tabpanel">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Unit Price (ETB) <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          v-model="formData.unit_price"
                          min="0"
                          step="0.01"
                          required
                          :class="{ 'is-invalid': errors.unit_price }">
                    </div>
                    <div class="invalid-feedback" v-if="errors.unit_price">
                      {{ errors.unit_price }}
                    </div>
                    <small class="form-text text-muted">Reference price for this service</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Coverage Type</label>
                    <select class="form-select" v-model="coverageData.coverage_type">
                      <option value="">Not Set</option>
                      <option value="fully_covered">Fully Covered</option>
                      <option value="partially_covered">Partially Covered</option>
                      <option value="excluded">Excluded</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3" v-if="coverageData.coverage_type === 'partially_covered'">
                    <label class="form-label">Copayment Percentage (%)</label>
                    <div class="input-group">
                      <input
                          type="number"
                          class="form-control"
                          v-model="coverageData.copayment_percentage"
                          min="0"
                          max="100"
                          step="5">
                      <span class="input-group-text">%</span>
                    </div>
                    <small class="form-text text-muted">Percentage patient pays</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Annual Coverage Limit (ETB)</label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          v-model="coverageData.annual_limit"
                          min="0"
                          step="100">
                    </div>
                    <small class="form-text text-muted">Maximum annual coverage amount</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Per Visit Limit (ETB)</label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          v-model="coverageData.per_visit_limit"
                          min="0"
                          step="10">
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Frequency Limit</label>
                    <input
                        type="number"
                        class="form-control"
                        v-model="coverageData.frequency_limit"
                        min="0"
                        placeholder="e.g., 12">
                    <small class="form-text text-muted">Max occurrences per year</small>
                  </div>

                  <div class="col-12">
                    <div class="form-check mb-2">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          v-model="coverageData.preauth_required">
                      <label class="form-check-label">
                        Pre-authorization Required
                      </label>
                    </div>

                    <div class="form-check mb-2">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          v-model="coverageData.requires_referral">
                      <label class="form-check-label">
                        Referral Required
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="resetForm">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="validateAndSave" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else :class="isEditMode ? 'bx bx-save' : 'bx bx-plus-circle'" class="me-1"></i>
            {{ saving ? 'Saving...' : (isEditMode ? 'Update Service' : 'Add Service') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ServiceFormModal",
  props: {
    service: {
      type: Object,
      default: null
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    categories: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      formData: {
        service_code: '',
        service_name: '',
        service_category: '',
        description: '',
        icd_code: '',
        cpt_code: '',
        snomed_code: '',
        local_code: '',
        unit_price: 0,
        is_active: 1
      },
      coverageData: {
        coverage_type: '',
        copayment_percentage: 0,
        preauth_required: false,
        annual_limit: null,
        per_visit_limit: null,
        frequency_limit: null,
        requires_referral: false
      },
      errors: {},
      saving: false,
      modal: null
    };
  },

  mounted() {
    this.modal = new bootstrap.Modal(document.getElementById('serviceFormModal'));
  },

  watch: {
    service: {
      handler(newVal) {
        if (newVal && this.isEditMode) {
          this.populateForm(newVal);
        }
      },
      immediate: true
    }
  },

  methods: {
    openModal() {
      if (!this.isEditMode) {
        this.resetForm();
      }
      this.modal.show();
    },

    closeModal() {
      this.modal.hide();
      this.resetForm();
    },

    populateForm(service) {
      this.formData = {
        service_id: service.service_id,
        service_code: service.service_code || '',
        service_name: service.service_name || '',
        service_category: service.service_category || '',
        description: service.description || '',
        icd_code: service.icd_code || '',
        cpt_code: service.cpt_code || '',
        snomed_code: service.snomed_code || '',
        local_code: service.local_code || '',
        unit_price: service.unit_price || 0,
        is_active: service.is_active || 1
      };

      // Populate coverage data if exists
      this.coverageData = {
        coverage_type: service.cbhi_coverage_status || '',
        copayment_percentage: service.cbhi_copayment_percentage || 0,
        preauth_required: service.cbhi_preauth_required || false,
        annual_limit: service.cbhi_coverage_limit || null,
        per_visit_limit: service.per_visit_limit || null,
        frequency_limit: service.cbhi_frequency_limit || null,
        requires_referral: service.requires_referral || false
      };
    },

    resetForm() {
      this.formData = {
        service_code: '',
        service_name: '',
        service_category: '',
        description: '',
        icd_code: '',
        cpt_code: '',
        snomed_code: '',
        local_code: '',
        unit_price: 0,
        is_active: 1
      };
      this.coverageData = {
        coverage_type: '',
        copayment_percentage: 0,
        preauth_required: false,
        annual_limit: null,
        per_visit_limit: null,
        frequency_limit: null,
        requires_referral: false
      };
      this.errors = {};
    },

    validateForm() {
      this.errors = {};

      if (!this.formData.service_code) {
        this.errors.service_code = 'Service code is required';
      }

      if (!this.formData.service_name) {
        this.errors.service_name = 'Service name is required';
      }

      if (!this.formData.service_category) {
        this.errors.service_category = 'Service category is required';
      }

      if (!this.formData.unit_price || this.formData.unit_price < 0) {
        this.errors.unit_price = 'Valid unit price is required';
      }

      return Object.keys(this.errors).length === 0;
    },

    validateAndSave() {
      if (!this.validateForm()) {
        // Switch to the tab with the first error
        if (this.errors.service_code || this.errors.service_name || this.errors.service_category) {
          document.querySelector('[href="#basic-info"]').click();
        } else if (this.errors.unit_price) {
          document.querySelector('[href="#pricing-coverage"]').click();
        }
        return;
      }

      this.saveService();
    },

    async saveService() {
      this.saving = true;

      const data = {
        "function": this.isEditMode ? "updateService" : "createService",
        ...this.formData,
        ...this.coverageData
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.$emit('service-saved', {
            message: this.isEditMode ? 'Service updated successfully' : 'Service created successfully',
            service: res.data.data.service
          });
          this.closeModal();
        } else {
          this.$emit('service-error', {
            message: res.data.message || 'Failed to save service'
          });
        }
      } catch (e) {
        this.$emit('service-error', {
          message: e.message || 'An error occurred while saving the service'
        });
      } finally {
        this.saving = false;
      }
    },

    // Code lookup methods
    lookupICD() {
      // Open ICD lookup modal or redirect to ICD lookup page
      window.open('https://icd.who.int/browse11/l-m/en', '_blank');
    },

    lookupCPT() {
      // Open CPT lookup modal or redirect to CPT lookup page
      window.open('https://www.aapc.com/codes/', '_blank');
    },

    lookupSNOMED() {
      // Open SNOMED lookup modal or redirect to SNOMED lookup page
      window.open('https://browser.ihtsdotools.org/', '_blank');
    }
  }
};
</script>

<style scoped>
.nav-tabs .nav-link {
  color: #495057;
}

.nav-tabs .nav-link.active {
  font-weight: 600;
}

.form-check-input {
  cursor: pointer;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}
</style>